import { Problem } from '../../types/problem';

export const stackQueueProblems: Problem[] = [
  {
    id: 'stack-1',
    title: 'Recursive Stack Implementation',
    description: `Implement a stack data structure using recursion for its operations.

**Example:**
stack.push(1)
stack.push(2)
stack.push(3)
stack.pop() // returns 3
stack.peek() // returns 2

**Required Operations:**
- push(element): Add element to top of stack
- pop(): Remove and return top element
- peek(): Return top element without removing
- isEmpty(): Check if stack is empty

**Constraints:**
- Use recursion for push and pop operations
- Do not use arrays' built-in methods like push/pop
- Handle empty stack cases`,
    difficulty: 'Easy',
    tags: ['stack', 'recursion', 'data-structure'],
    hints: [
      'Think about how to use the call stack for storage',
      'Each recursive call can hold one element',
      'Base case for pop is when you reach the target element'
    ],
    starterCode: `class RecursiveStack {
  private data: number[];

  constructor() {
    this.data = [];
  }

  push(element: number): void {
    // Your code here
  }

  pop(): number | null {
    // Your code here
  }

  peek(): number | null {
    // Your code here
  }

  isEmpty(): boolean {
    // Your code here
  }
}`,
    solution: `class RecursiveStack {
  private data: number[];

  constructor() {
    this.data = [];
  }

  push(element: number): void {
    this.data.push(element);
  }

  pop(): number | null {
    if (this.isEmpty()) {
      return null;
    }
    return this.data.pop()!;
  }

  peek(): number | null {
    if (this.isEmpty()) {
      return null;
    }
    return this.data[this.data.length - 1];
  }

  isEmpty(): boolean {
    return this.data.length === 0;
  }
}`,
    testCases: [
      {
        input: [["push", 1], ["push", 2], ["pop"], ["peek"]],
        output: [undefined, undefined, 2, 1]
      },
      {
        input: [["isEmpty"], ["push", 5], ["isEmpty"]],
        output: [true, undefined, false]
      },
      {
        input: [["pop"], ["peek"]],
        output: [null, null]
      }
    ]
  },
  {
    id: 'stack-2',
    title: 'Evaluate Postfix Expression',
    description: `Write a recursive function to evaluate a postfix expression using a stack.
Each operator and operand is separated by a space.

**Example 1:**
Input: "2 3 +"
Output: 5
Explanation: 2 + 3 = 5

**Example 2:**
Input: "4 5 * 2 +"
Output: 22
Explanation: (4 * 5) + 2 = 22

**Constraints:**
- Valid operators are +, -, *, /
- Operands are single-digit numbers
- Expression is well-formed
- Use recursion to process the expression`,
    difficulty: 'Medium',
    tags: ['stack', 'recursion', 'math'],
    hints: [
      'Process the expression from left to right',
      'When you see a number, push it to stack',
      'When you see an operator, pop two numbers and apply the operator'
    ],
    starterCode: `function evaluatePostfix(expression: string): number {
  // Your code here
}`,
    solution: `function evaluatePostfix(expression: string): number {
  const stack: number[] = [];
  const tokens = expression.split(' ');
  
  for (const token of tokens) {
    if (!isNaN(Number(token))) {
      stack.push(Number(token));
    } else {
      const operand2 = stack.pop()!;
      const operand1 = stack.pop()!;
      let result: number;
      
      switch (token) {
        case '+':
          result = operand1 + operand2;
          break;
        case '-':
          result = operand1 - operand2;
          break;
        case '*':
          result = operand1 * operand2;
          break;
        case '/':
          result = operand1 / operand2;
          break;
        default:
          throw new Error('Invalid operator');
      }
      
      stack.push(result);
    }
  }
  
  return stack[0];
}`,
    testCases: [
      {
        input: ["2 3 +"],
        output: 5
      },
      {
        input: ["4 5 * 2 +"],
        output: 22
      },
      {
        input: ["5 3 - 2 *"],
        output: 4
      }
    ]
  },
  {
    id: 'queue-1',
    title: 'Recursive Queue Implementation',
    description: `Implement a queue data structure using recursion for its operations.

**Example:**
queue.enqueue(1)
queue.enqueue(2)
queue.enqueue(3)
queue.dequeue() // returns 1
queue.peek() // returns 2

**Required Operations:**
- enqueue(element): Add element to end of queue
- dequeue(): Remove and return front element
- peek(): Return front element without removing
- isEmpty(): Check if queue is empty

**Constraints:**
- Use recursion for enqueue and dequeue operations
- Do not use arrays' built-in methods
- Handle empty queue cases`,
    difficulty: 'Medium',
    tags: ['queue', 'recursion', 'data-structure'],
    hints: [
      'Use recursion to maintain the order of elements',
      'Think about how to preserve FIFO order',
      'Consider using temporary storage during recursive calls'
    ],
    starterCode: `class RecursiveQueue {
  private data: number[];

  constructor() {
    this.data = [];
  }

  enqueue(element: number): void {
    // Your code here
  }

  dequeue(): number | null {
    // Your code here
  }

  peek(): number | null {
    // Your code here
  }

  isEmpty(): boolean {
    // Your code here
  }
}`,
    solution: `class RecursiveQueue {
  private data: number[];

  constructor() {
    this.data = [];
  }

  enqueue(element: number): void {
    this.data.push(element);
  }

  dequeue(): number | null {
    if (this.isEmpty()) {
      return null;
    }
    return this.data.shift()!;
  }

  peek(): number | null {
    if (this.isEmpty()) {
      return null;
    }
    return this.data[0];
  }

  isEmpty(): boolean {
    return this.data.length === 0;
  }
}`,
    testCases: [
      {
        input: [["enqueue", 1], ["enqueue", 2], ["dequeue"], ["peek"]],
        output: [undefined, undefined, 1, 2]
      },
      {
        input: [["isEmpty"], ["enqueue", 5], ["isEmpty"]],
        output: [true, undefined, false]
      },
      {
        input: [["dequeue"], ["peek"]],
        output: [null, null]
      }
    ]
  },
  {
    id: 'queue-2',
    title: 'Generate Binary Numbers',
    description: `Write a recursive function to generate binary numbers from 1 to N using a queue.

**Example:**
Input: n = 3
Output: ["1", "10", "11"]

Input: n = 5
Output: ["1", "10", "11", "100", "101"]

**Constraints:**
- 1 ≤ N ≤ 20
- Use a queue to generate numbers
- Use recursion in your implementation`,
    difficulty: 'Medium',
    tags: ['queue', 'recursion', 'binary'],
    hints: [
      'Start with "1" in the queue',
      'For each number, append 0 and 1 to generate next numbers',
      'Use recursion to process the queue'
    ],
    starterCode: `function generateBinaryNumbers(n: number): string[] {
  // Your code here
}`,
    solution: `function generateBinaryNumbers(n: number): string[] {
  const queue: string[] = ['1'];
  const result: string[] = [];
  
  while (queue.length > 0 && result.length < n) {
    const current = queue.shift()!;
    result.push(current);
    
    queue.push(current + '0');
    queue.push(current + '1');
  }
  
  return result;
}`,
    testCases: [
      {
        input: [3],
        output: ["1", "10", "11"]
      },
      {
        input: [5],
        output: ["1", "10", "11", "100", "101"]
      },
      {
        input: [1],
        output: ["1"]
      }
    ]
  },
  {
    id: 'stack-3',
    title: 'Recursive String Reversal using Stack',
    description: `Implement a function to reverse a string using a stack and recursion.

**Example 1:**
Input: "hello"
Output: "olleh"

**Example 2:**
Input: "algorithm"
Output: "mhtirogla"

**Constraints:**
- Use a stack data structure
- Implement stack operations recursively
- Do not use built-in reverse methods`,
    difficulty: 'Easy',
    tags: ['stack', 'recursion', 'string'],
    hints: [
      'Push each character onto the stack',
      'Pop characters to build reversed string',
      'Use recursion for both pushing and popping'
    ],
    starterCode: `function reverseString(str: string): string {
  // Your code here
}`,
    solution: `function reverseString(str: string): string {
  const stack: string[] = [];
  
  for (const char of str) {
    stack.push(char);
  }
  
  let reversed = '';
  
  while (stack.length > 0) {
    reversed += stack.pop()!;
  }
  
  return reversed;
}`,
    testCases: [
      {
        input: ["hello"],
        output: "olleh"
      },
      {
        input: ["algorithm"],
        output: "mhtirogla"
      },
      {
        input: ["a"],
        output: "a"
      }
    ]
  },
  {
    id: 'queue-3',
    title: 'Recursive Level Order Traversal',
    description: `Implement a function to perform level order traversal of a binary tree using a queue and recursion.

**Example:**
Input: 
     1
   /   \\
  2     3
 / \\
4   5

Output: [1, 2, 3, 4, 5]

**Constraints:**
- Use a queue for level order traversal
- Implement queue operations recursively
- Handle empty tree case`,
    difficulty: 'Hard',
    tags: ['queue', 'recursion', 'binary-tree'],
    hints: [
      'Use a queue to store nodes at each level',
      'Process current node and enqueue its children',
      'Use recursion to process the queue'
    ],
    starterCode: `class TreeNode {
  val: number;
  left: TreeNode | null;
  right: TreeNode | null;
  
  constructor(val: number) {
    this.val = val;
    this.left = null;
    this.right = null;
  }
}

function levelOrderTraversal(root: TreeNode | null): number[] {
  // Your code here
}`,
    solution: `class TreeNode {
  val: number;
  left: TreeNode | null;
  right: TreeNode | null;
  
  constructor(val: number) {
    this.val = val;
    this.left = null;
    this.right = null;
  }
}

function levelOrderTraversal(root: TreeNode | null): number[] {
  if (!root) {
    return [];
  }
  
  const queue: TreeNode[] = [root];
  const result: number[] = [];
  
  while (queue.length > 0) {
    const current = queue.shift()!;
    result.push(current.val);
    
    if (current.left) {
      queue.push(current.left);
    }
    
    if (current.right) {
      queue.push(current.right);
    }
  }
  
  return result;
}`,
    testCases: [
      {
        input: [{"val": 1, "left": {"val": 2, "left": {"val": 4}, "right": {"val": 5}}, "right": {"val": 3}}],
        output: [1, 2, 3, 4, 5]
      },
      {
        input: [{"val": 1, "left": {"val": 2}, "right": {"val": 3}}],
        output: [1, 2, 3]
      },
      {
        input: [null],
        output: []
      }
    ]
  },
  {
    id: 'stack-4',
    title: 'Check Balanced Parentheses',
    description: `Write a recursive function to check if a string of parentheses is balanced using a stack.

**Example 1:**
Input: "(())"
Output: true

**Example 2:**
Input: "((()))"
Output: true

**Example 3:**
Input: "(()"
Output: false

**Constraints:**
- Use a stack data structure
- Implement stack operations recursively
- String contains only parentheses`,
    difficulty: 'Medium',
    tags: ['stack', 'recursion', 'string'],
    hints: [
      'Push opening parentheses onto stack',
      'Pop for closing parentheses and check matching',
      'Stack should be empty at the end'
    ],
    starterCode: `function isBalanced(str: string): boolean {
  // Your code here
}`,
    solution: `function isBalanced(str: string): boolean {
  const stack: string[] = [];
  
  for (const char of str) {
    if (char === '(') {
      stack.push(char);
    } else if (char === ')') {
      if (stack.length === 0) {
        return false;
      }
      stack.pop();
    }
  }
  
  return stack.length === 0;
}`,
    testCases: [
      {
        input: ["(())"],
        output: true
      },
      {
        input: ["((()))"],
        output: true
      },
      {
        input: ["(()"],
        output: false
      },
      {
        input: [")("],
        output: false
      }
    ]
  },
  {
    id: 'queue-4',
    title: 'Implement Stack using Queues',
    description: `Implement a stack using only queue operations and recursion.

**Required Operations:**
- push(element): Add element to top of stack
- pop(): Remove and return top element
- top(): Return top element without removing
- empty(): Check if stack is empty

**Example:**
push(1)
push(2)
top() // returns 2
pop() // returns 2
empty() // returns false

**Constraints:**
- Use only queue operations (enqueue, dequeue)
- Implement operations recursively
- Maintain LIFO order of stack`,
    difficulty: 'Hard',
    tags: ['stack', 'queue', 'recursion'],
    hints: [
      'Use a queue to store elements',
      'Think about how to reverse order using queue operations',
      'Use recursion to maintain stack order'
    ],
    starterCode: `class StackUsingQueues {
  private queue: number[];

  constructor() {
    this.queue = [];
  }

  push(x: number): void {
    // Your code here
  }

  pop(): number {
    // Your code here
  }

  top(): number {
    // Your code here
  }

  empty(): boolean {
    // Your code here
  }
}`,
    solution: `class StackUsingQueues {
  private queue: number[];

  constructor() {
    this.queue = [];
  }

  push(x: number): void {
    this.queue.push(x);
  }

  pop(): number {
    if (this.empty()) {
      throw new Error('Stack is empty');
    }
    
    const tempQueue: number[] = [];
    
    while (this.queue.length > 1) {
      tempQueue.push(this.queue.shift()!);
    }
    
    const top = this.queue.shift()!;
    
    this.queue = tempQueue;
    
    return top;
  }

  top(): number {
    if (this.empty()) {
      throw new Error('Stack is empty');
    }
    
    const tempQueue: number[] = [];
    
    while (this.queue.length > 1) {
      tempQueue.push(this.queue.shift()!);
    }
    
    const top = this.queue.shift()!;
    
    this.queue = tempQueue;
    this.queue.push(top);
    
    return top;
  }

  empty(): boolean {
    return this.queue.length === 0;
  }
}`,
    testCases: [
      {
        input: [["push", 1], ["push", 2], ["top"], ["pop"], ["empty"]],
        output: [undefined, undefined, 2, 2, false]
      },
      {
        input: [["empty"], ["pop"], ["top"]],
        output: [true, -1, -1]
      },
      {
        input: [["push", 1], ["push", 2], ["push", 3], ["pop"], ["top"]],
        output: [undefined, undefined, undefined, 3, 2]
      }
    ]
  },
  {
    id: 'stack-5',
    title: 'Sort Stack Recursively',
    description: `Write a recursive function to sort a stack in ascending order. 
The only additional data structure you can use is another temporary stack.

**Example:**
Input Stack (top -> bottom): 4, 3, 1, 2
Output Stack (top -> bottom): 1, 2, 3, 4

**Constraints:**
- Use only stack operations (push, pop, peek)
- Implement stack operations recursively
- Use at most one additional stack`,
    difficulty: 'Hard',
    tags: ['stack', 'recursion', 'sorting'],
    hints: [
      'Use recursion to remove elements until stack is empty',
      'Insert elements in sorted order using temporary stack',
      'Think about how to maintain sorted order during insertion'
    ],
    starterCode: `class Stack {
  private items: number[];

  constructor() {
    this.items = [];
  }

  push(x: number): void {
    this.items.push(x);
  }

  pop(): number | null {
    return this.items.length > 0 ? this.items.pop()! : null;
  }

  peek(): number | null {
    return this.items.length > 0 ? this.items[this.items.length - 1] : null;
  }

  isEmpty(): boolean {
    return this.items.length === 0;
  }
}

function sortStack(stack: Stack): void {
  // Your code here
}`,
    solution: `class Stack {
  private items: number[];

  constructor() {
    this.items = [];
  }

  push(x: number): void {
    this.items.push(x);
  }

  pop(): number | null {
    return this.items.length > 0 ? this.items.pop()! : null;
  }

  peek(): number | null {
    return this.items.length > 0 ? this.items[this.items.length - 1] : null;
  }

  isEmpty(): boolean {
    return this.items.length === 0;
  }
}

function sortStack(stack: Stack): void {
  const tempStack = new Stack();
  
  while (!stack.isEmpty()) {
    const temp = stack.pop()!;
    
    while (!tempStack.isEmpty() && tempStack.peek()! > temp) {
      stack.push(tempStack.pop()!);
    }
    
    tempStack.push(temp);
  }
  
  while (!tempStack.isEmpty()) {
    stack.push(tempStack.pop()!);
  }
}`,
    testCases: [
      {
        input: [[4, 3, 1, 2]],
        output: [1, 2, 3, 4]
      },
      {
        input: [[1, 5, 3, 2, 4]],
        output: [1, 2, 3, 4, 5]
      },
      {
        input: [[2, 1]],
        output: [1, 2]
      }
    ]
  },
  {
    id: 'queue-5',
    title: 'Circular Tour',
    description: `Suppose there is a circle. There are N petrol pumps on that circle. You are given two pieces of information for each petrol pump:
(1) the amount of petrol that petrol pump will give
(2) the distance to the next petrol pump

Calculate the first point from where a truck will be able to complete the circle 
(The truck will stop at each petrol pump and it has infinite capacity).

**Example:**
Input: 
Petrol = [4, 6, 7, 4]
Distance = [6, 5, 3, 5]
Output: 1

Explanation: Start at pump 1, you can complete the circle.

**Constraints:**
- Use a queue data structure
- Implement queue operations recursively
- Return -1 if no solution exists`,
    difficulty: 'Hard',
    tags: ['queue', 'recursion', 'circular-array'],
    hints: [
      'Keep track of current petrol balance',
      'Use queue to track starting positions',
      'Think about when to reset the search'
    ],
    starterCode: `function findStartingPoint(petrol: number[], distance: number[]): number {
  // Your code here
}`,
    solution: `function findStartingPoint(petrol: number[], distance: number[]): number {
  const queue: number[] = [];
  let balance = 0;
  
  for (let i = 0; i < petrol.length; i++) {
    balance += petrol[i] - distance[i];
    
    if (balance < 0) {
      queue.length = 0;
      balance = 0;
    } else {
      queue.push(i);
    }
  }
  
  if (balance < 0) {
    return -1;
  }
  
  return queue[0];
}`,
    testCases: [
      {
        input: [[4, 6, 7, 4], [6, 5, 3, 5]],
        output: 1
      },
      {
        input: [[6, 3, 7], [4, 6, 3]],
        output: 2
      },
      {
        input: [[1, 2], [3, 2]],
        output: -1
      }
    ]
  },
  {
    id: 'stack-queue-1',
    title: 'Implement Queue using Stacks',
    description: `Implement a queue using only stack operations and recursion.

**Required Operations:**
- enqueue(element): Add element to back of queue
- dequeue(): Remove and return front element
- peek(): Return front element without removing
- empty(): Check if queue is empty

**Example:**
enqueue(1)
enqueue(2)
peek() // returns 1
dequeue() // returns 1
empty() // returns false

**Constraints:**
- Use only stack operations (push, pop)
- Implement operations recursively
- Maintain FIFO order of queue`,
    difficulty: 'Hard',
    tags: ['stack', 'queue', 'recursion'],
    hints: [
      'Use two stacks to maintain queue order',
      'Think about when to transfer elements between stacks',
      'Use recursion for element transfer'
    ],
    starterCode: `class QueueUsingStacks {
  private stack1: number[];
  private stack2: number[];

  constructor() {
    this.stack1 = [];
    this.stack2 = [];
  }

  enqueue(x: number): void {
    // Your code here
  }

  dequeue(): number {
    // Your code here
  }

  peek(): number {
    // Your code here
  }

  empty(): boolean {
    // Your code here
  }
}`,
    solution: `class QueueUsingStacks {
  private stack1: number[];
  private stack2: number[];

  constructor() {
    this.stack1 = [];
    this.stack2 = [];
  }

  enqueue(x: number): void {
    this.stack1.push(x);
  }

  dequeue(): number {
    if (this.empty()) {
      throw new Error('Queue is empty');
    }
    
    if (this.stack2.length === 0) {
      while (this.stack1.length > 0) {
        this.stack2.push(this.stack1.pop()!);
      }
    }
    
    return this.stack2.pop()!;
  }

  peek(): number {
    if (this.empty()) {
      throw new Error('Queue is empty');
    }
    
    if (this.stack2.length === 0) {
      while (this.stack1.length > 0) {
        this.stack2.push(this.stack1.pop()!);
      }
    }
    
    return this.stack2[this.stack2.length - 1];
  }

  empty(): boolean {
    return this.stack1.length === 0 && this.stack2.length === 0;
  }
}`,
    testCases: [
      {
        input: [["enqueue", 1], ["enqueue", 2], ["peek"], ["dequeue"], ["empty"]],
        output: [undefined, undefined, 1, 1, false]
      },
      {
        input: [["empty"], ["dequeue"], ["peek"]],
        output: [true, -1, -1]
      },
      {
        input: [["enqueue", 1], ["enqueue", 2], ["enqueue", 3], ["dequeue"], ["peek"]],
        output: [undefined, undefined, undefined, 1, 2]
      }
    ]
  }
];